import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';

import { getAppId, getAppVersion } from '@dynatrace-sdk/app-environment';

import * as styles from './AppIcon.sty.js';
import { FallbackIcon } from './FallbackIcon.js';
import { fetchIcon } from './icon-utils.js';
import { Skeleton } from '../../../content/skeleton/Skeleton.js';
import { AriaLabelingProps } from '../../../core/types/a11y-props.js';
import { DataTestId } from '../../../core/types/data-props.js';
import { StylingProps } from '../../../core/types/styling-props.js';
import { WithChildren } from '../../../core/types/with-children.js';
import { isProduction } from '../../../core/utils/is-node-environment.js';

const COMPONENT_NAME = 'AppIcon';

// #region Typings
/**
 * Accepted properties for the AppIcon.
 * @public
 */
export interface AppIconProps
  extends AriaLabelingProps,
    WithChildren,
    StylingProps,
    DataTestId {
  /**
   * The path to the app icon. If not specified, the icon from the app registry is retrieved automatically.
   */
  src?: string;
  /**
   * The alternative text representation for the image when it's not loaded. If not specified, the app name is used.
   */
  alt?: string;
}
// #endregion

const AppIconRoot = (props: AppIconProps) => {
  const [fadeIn, setFadeIn] = useState(false);

  const sdkAppId = useMemo(getAppId, []);
  const sdkAppVersion = useMemo(getAppVersion, []);
  const {
    src = `/platform/app-engine/registry/v1/app-icons/${sdkAppId}?appVersion=${encodeURIComponent(
      sdkAppVersion,
    )}`,
    alt,
    children,
    className: consumerClassName,
    style: consumerStyle = {},
    'data-testid': dataTestId,
    ...remainingProps
  }: AppIconProps = props;
  const {
    data: iconSrc,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['app-icon', src],
    queryFn: () => fetchIcon(src),
    retry: isProduction() ? undefined : false,
  });

  useEffect(() => {
    if (isLoading) {
      setFadeIn(isLoading);
    }
  }, [isLoading]);

  if (isLoading) {
    return <Skeleton width={20} height={20} />;
  }

  if (isError) {
    return <FallbackIcon size={20} />;
  }

  return (
    <img
      src={iconSrc}
      data-testid={dataTestId}
      width={20}
      height={20}
      alt={alt}
      className={clsx(styles.icon({ fadeIn }), consumerClassName)}
      style={consumerStyle}
      {...remainingProps}
      data-dt-component={COMPONENT_NAME}
      data-dt-name={COMPONENT_NAME}
    />
  );
};

const appIconQueryClient = new QueryClient();

/**
 * The `AppIcon` component renders the icon used for the app on the platform.
 * You can also provide a link to an icon to render that icon instead.
 * @public
 */
export const AppIcon = (props: AppIconProps) => {
  return (
    <QueryClientProvider client={appIconQueryClient}>
      <AppIconRoot {...props} />
    </QueryClientProvider>
  );
};

AppIcon.displayName = COMPONENT_NAME;
